/* global google */
import { Component } from 'preact'
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import loadGoogleMaps from '../lib/google-maps'
import snazzyMapStyle from '../snazzy-map-style'
import cx from 'classnames'
import s from './map.sass'

export default class extends Component {
  markers = []

  clusterer = undefined

  icons = undefined

  iconSize = 36

  componentDidMount () {
    loadGoogleMaps(this.updateMap)
  }

  componentDidUpdate (prevProps) {
    // Auto zoom if zoom has been defined
    if (!this.state.autoZoomed && this.props.zoom && this.map) {
      this.setState({ autoZoomed: true })
      this.map.setZoom(this.props.zoom)
    }

    if (JSON.stringify(prevProps.pins) !== JSON.stringify(this.props.pins)) {
      loadGoogleMaps(this.updateMap)
    } else if (prevProps.highlighted !== this.props.highlighted && this.markers) {
      this.updateMap(true)
    }

    if (prevProps.setCenter !== this.props.setCenter) {
      const pin = this.props.pins.find(pin => pin.id === this.props.setCenter)

      this.map.setCenter(pin.location)
      this.map.setZoom(16)
    }
  }

  setMap () {
    this.map = new google.maps.Map(this.base, {
      center: { lat: 57.3620777, lng: 14.4731505 },
      zoom: 10,
      disableDefaultUI: true,
      zoomControl: true,
      styles: snazzyMapStyle
    })

    // "onLoad" from https://stackoverflow.com/a/7262773
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      setTimeout(() => this.setState({ loaded: true }), 10)
    })

    google.maps.event.addListener(this.map, 'bounds_changed', () => {
      const bounds = this.map.getBounds()

      // Filter out any pins that are out of bounds
      const filteredPins = this.props.pins && this.props.pins.filter(p => (
        p.location && bounds.contains(p.location)
      ))

      // call back with pins inside bouds, if any.
      if (this.props.onPinsInMap) this.props.onPinsInMap(filteredPins || [])
    })
  }

  clearMarkers () {
    this.markers.forEach(m => m.setMap(null))
    this.markers = []
  }

  setIcons () {
    this.icons = {
      basic: {
        url: '/images/map-pin-basic.png',
        size: new google.maps.Size(this.iconSize, this.iconSize),
        scaledSize: new google.maps.Size(this.iconSize, this.iconSize),
        anchor: new google.maps.Point(this.iconSize / 2, this.iconSize)
      },
      premium: {
        url: '/images/map-pin-premium.png',
        size: new google.maps.Size(this.iconSize, this.iconSize),
        scaledSize: new google.maps.Size(this.iconSize, this.iconSize),
        anchor: new google.maps.Point(this.iconSize / 2, this.iconSize)
      },
      disabled: {
        url: '/images/map-pin-disabled.png',
        size: new google.maps.Size(this.iconSize, this.iconSize),
        scaledSize: new google.maps.Size(this.iconSize, this.iconSize),
        anchor: new google.maps.Point(this.iconSize / 2, this.iconSize)
      }
    }
  }

  getIcon (pin, forceHighlight = false) {
    const { highlighted } = this.props

    const icon = pin.disabled
      ? { ...this.icons.disabled }
      : pin.type
        ? { ...this.icons[pin.type] }
        : { ...this.icons.disabled } // Default to disabled if type is missing, shouldn't happend but...

    if (forceHighlight || pin.id === highlighted) {
      const scaledSize = this.iconSize * 1.25
      icon.size = new google.maps.Size(scaledSize, scaledSize)
      icon.scaledSize = new google.maps.Size(scaledSize, scaledSize)
      icon.anchor = new google.maps.Point(scaledSize / 2, scaledSize)
    }

    return icon
  }

  setClusterer () {
    this.clusterer = new MarkerClusterer({
      markers: this.markers,
      map: this.map,
      renderer: {
        render: ({ count, position }) => {
          const svg = window.btoa(`
            <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" fill="none" viewBox="0 0 44 44">
              <g filter="url(#filter0_d_650_4149)">
                <path fill="#fff" d="M40 18c0 9.941-8.059 18-18 18S4 27.941 4 18 12.059 0 22 0s18 8.059 18 18z"/>
                <path fill="#FF5200" d="M37 18c0 8.284-6.716 15-15 15-8.284 0-15-6.716-15-15C7 9.716 13.716 3 22 3c8.284 0 15 6.716 15 15z"/>
              </g>
              <defs>
                <filter id="filter0_d_650_4149" width="44" height="44" x="0" y="0" color-interpolation-filters="sRGB" filterUnits="userSpaceOnUse">
                  <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                  <feColorMatrix in="SourceAlpha" result="hardAlpha" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"/>
                  <feOffset dy="4"/>
                  <feGaussianBlur stdDeviation="2"/>
                  <feComposite in2="hardAlpha" operator="out"/>
                  <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                  <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_650_4149"/>
                  <feBlend in="SourceGraphic" in2="effect1_dropShadow_650_4149" result="shape"/>
                </filter>
              </defs>
            </svg>
          `)

          return new google.maps.Marker({
            position,
            icon: {
              url: `data:image/svg+xml;base64,${svg}`,
              scaledSize: new google.maps.Size(44, 44)
            },
            label: {
              text: String(count),
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              className: 'clusterLabel' // Needed to offset label due to drop shadow effect in svg offsetting the "center"-
            },
            title: `${count} gym i området.`,
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count
          })
        }
      }
    })
  }

  updateMap = (useCachedMarkers) => {
    const { pins, highlighted } = this.props

    if (!this.map) this.setMap()

    if (!this.icons) this.setIcons()

    if (useCachedMarkers) {
      for (const marker of this.markers) {
        const pin = pins && pins.find(p => p.id === marker.pinId)
        if (pin) {
          marker.setIcon(this.getIcon(pin))
          marker.setZIndex(
            pin.id === highlighted
              ? google.maps.Marker.MAX_ZINDEX + 1
              : undefined
          )
        }
      }
    } else {
      this.clearMarkers()

      if (pins) {
        const bounds = new google.maps.LatLngBounds()
        let amountPinsForBounds = this.props.amountPinsForBounds || 9999

        pins.forEach(p => {
          if (!p.location) return

          const marker = new google.maps.Marker({
            icon: this.getIcon(p),
            position: p.location,
            map: this.map,
            optimized: false,
            zIndex: p.id === highlighted
              ? google.maps.Marker.MAX_ZINDEX + 1
              : undefined
          })

          marker.addListener('click', () => {
            if (!p.disabled) this.props.onPinClicked(p)
          })

          marker.addListener('mouseover', () => {
            marker.setIcon(this.getIcon(p, true))
            marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1)
          })

          marker.addListener('mouseout', () => {
            marker.setIcon(this.getIcon(p))
            marker.setZIndex(
              p.id === this.props.highlighted
                ? google.maps.Marker.MAX_ZINDEX + 1
                : undefined
            )
          })

          marker.pinId = p.id
          this.markers.push(marker)

          if (--amountPinsForBounds >= 0) {
            bounds.extend(p.location)
          }
        })

        if (!this.clusterer) this.setClusterer()

        this.clusterer.clearMarkers()
        this.clusterer.addMarkers(this.markers)

        setTimeout(() => {
          this.map.fitBounds(bounds)
        }, 200) // Adding this timeout to fix safari until I found out what the real culprit is.

        if (this.map.getZoom() >= 17) {
          this.map.setZoom(17)
        }
      }
    }
  }

  render ({ className }, { loaded }) {
    return (
      <div class={cx(s.map, className, !loaded && s.hidden)} />
    )
  }
}



// WEBPACK FOOTER //
// ./components/map.js